import React, { useState, useEffect } from 'react';
import './Comment.css';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import CommentForm from '../comment/CommentForm';
import CommentList from '../comment/CommentList';
import CommentConflictPopup from '../comment/CommentConflictPopup';
import { fetchComments, postComment } from '../../utils/commentService';

function Comment() {
    const [comment, setComment] = useState('');
    const [rating, setRating] = useState(0);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [token, setToken] = useState(null);
    const [responseMessage, setResponseMessage] = useState('');
    const [showConflictPopup, setShowConflictPopup] = useState(false);
    const [conflictData, setConflictData] = useState(null);

    const googleClientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;

    const handleCommentChange = (e) => setComment(e.target.value);
    const handleRatingChange = (newRating) => setRating(newRating);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (token) {
            const commentData = {
                id_token: token,
                comment_text: comment,
                rating: rating.toString()
            };
            try {
                const result = await postComment(commentData);
                if (result && result.statusCode === 200) {
                    // Reload comments after submitting a new comment
                    setLoading(true);
                    const updatedComments = await fetchComments();
                    setComments(updatedComments); // Ensure comments are updated
                    setResponseMessage('');
                    setLoading(false);
                    setComment('');
                    setRating(0);
                } else {
                    setResponseMessage(result.message || 'Failed to post comment.');
                    if (result && result.statusCode === 409) {
                        // Handle conflict - user has already commented
                        const responseBody = typeof result.body === 'string' ? JSON.parse(result.body) : result.body;
                        setConflictData({
                            existingComment: responseBody.existing_comment,
                            newComment: comment,
                            newRating: rating
                        });
                        setShowConflictPopup(true);
                        setResponseMessage('');
                    }
                }
            } catch (err) {
                console.error('Error posting comment:', err);
                setResponseMessage(err.message || 'An error occurred.');
            }
        }
    };

    const handleLoginSuccess = (response) => {
        setIsAuthenticated(true);
        setToken(response.credential);
    };

    const handleLoginFailure = (response) => console.log('Login Failed:', response);

    const handleConflictUpdate = async () => {
        // User confirmed they want to update their existing comment
        const commentData = {
            id_token: token,
            comment_text: conflictData.newComment,
            rating: conflictData.newRating.toString(),
            action: 'update'
        };

        try {
            const result = await postComment(commentData);
            if (result && result.statusCode === 200) {
                // Reload comments after updating
                setLoading(true);
                const updatedComments = await fetchComments();
                setComments(updatedComments);
                setResponseMessage('Comment updated successfully!');
                setLoading(false);
                setComment('');
                setRating(0);
                setShowConflictPopup(false);
                setConflictData(null);
            } else {
                console.error('Failed to update comment:', result);
                setResponseMessage(result.message || 'Failed to update comment.');
                setShowConflictPopup(false);
            }
        } catch (err) {
            console.error('Error updating comment:', err);
            setResponseMessage(err.message || 'An error occurred.');
            setShowConflictPopup(false);
        }
    };

    const handleConflictCancel = () => {
        setShowConflictPopup(false);
        setConflictData(null);
        setResponseMessage('');
    };

    useEffect(() => {
        const normalizeComments = (response) => {
            if (response && typeof response.body === 'string') {
                return JSON.parse(response.body);
            }
            return response;
        };

        const loadComments = async () => {
            const response = await fetchComments();
            const data = normalizeComments(response);
            setComments(data);
            setLoading(false);
        };
        loadComments();
    }, []);

    // Handle missing client ID configuration
    if (!googleClientId) {
        return (
            <div className="comment-container">
                <h2>Configuration Error</h2>
                <p>Google OAuth Client ID is not configured. Please check your environment variables.</p>
            </div>
        );
    }

    return (
        <GoogleOAuthProvider clientId={googleClientId}>
            <div className="comment-container">
                <h2>Let us know your opinion!</h2>
                {isAuthenticated ? (
                    <>
                        <CommentForm
                            comment={comment}
                            rating={rating}
                            onCommentChange={handleCommentChange}
                            onRatingChange={handleRatingChange}
                            onSubmit={handleSubmit}
                        />
                        {responseMessage && <p className="response-message">{responseMessage}</p>}
                    </>
                ) : (
                    <GoogleLogin
                        text="signin"
                        theme='filled_black'
                        shape='pill'
                        onSuccess={handleLoginSuccess}
                        onError={handleLoginFailure}
                        className="google-login-button"
                    />
                )}
                <CommentList comments={comments} loading={loading} />

                {showConflictPopup && conflictData && (
                    <CommentConflictPopup
                        existingComment={conflictData.existingComment}
                        newComment={conflictData.newComment}
                        newRating={conflictData.newRating}
                        onSubmit={handleConflictUpdate}
                        onCancel={handleConflictCancel}
                    />
                )}
            </div>
        </GoogleOAuthProvider>
    );
}

export default Comment;